import { View, Text, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { GuidePageHeader, GuideCard, GuideTip, GuideNav, getGuideNav } from "./_components";

const PAGE_HREF = "/(web)/guide/roles";

/** Role detail section */
const RoleSection = ({ icon, title, color, borderColor, children }: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  color: string;
  borderColor: string;
  children: React.ReactNode;
}) => (
  <View className={`bg-white rounded-xl p-6 shadow-sm border mb-4`} style={{ borderColor }}>
    <View className="flex-row items-center gap-3 mb-4">
      <View
        className="w-12 h-12 rounded-xl items-center justify-center"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={24} color={color} />
      </View>
      <Text className="text-xl font-bold text-gray-900">{title}</Text>
    </View>
    {children}
  </View>
);

/** Capability list item */
const Capability = ({ text }: { text: string }) => (
  <View className="flex-row items-start gap-2 mb-2">
    <Ionicons name="checkmark-circle" size={18} color="#10B981" style={{ marginTop: 2 }} />
    <Text className="text-sm text-gray-600 leading-5 flex-1">{text}</Text>
  </View>
);

export default function RolesPage() {
  if (Platform.OS !== "web") return null;
  const nav = getGuideNav(PAGE_HREF);

  return (
    <View>
      <GuidePageHeader
        icon="shield"
        title="Rollen"
        subtitle="In de app zijn er drie rollen met verschillende rechten. Hier lees je wat elke rol kan doen."
        color="#D97706"
      />

      <Text className="text-base text-gray-600 leading-6 mb-8">
        Elke gebruiker heeft minimaal de rol "Familielid". Daarnaast kan een gebruiker ook 
        "Familie Admin" of "Super Admin" zijn. Deze rollen geven extra mogelijkheden binnen de app.
      </Text>

      {/* Familielid */}
      <RoleSection icon="person" title="Familielid" color="#3B82F6" borderColor="#BFDBFE">
        <Text className="text-sm text-gray-600 leading-5 mb-4">
          De standaardrol voor elk familielid. Als familielid heb je toegang tot alle basis functies 
          van de app.
        </Text>
        <Capability text="Chatten met familieleden in groep- en privéchats" />
        <Capability text="Spellen spelen (solo en versus andere familieleden)" />
        <Capability text="Foto's uploaden en bekijken in de familiegalerij" />
        <Capability text="Uitdagingen en opdrachten voltooien voor punten" />
        <Capability text="De agenda bekijken en eigen evenementen aanmaken" />
        <Capability text="Je teamprofiel bekijken en teamavatar aanpassen" />
        <Capability text="Meldingsinstellingen beheren" />
        <Capability text="Evenementen exporteren naar je eigen agenda" />
      </RoleSection>

      {/* Familie Admin */}
      <RoleSection icon="shield" title="Familie Admin" color="#D97706" borderColor="#FDE68A">
        <Text className="text-sm text-gray-600 leading-5 mb-4">
          Familie admins zijn de commissieleden die het weekend organiseren. Ze hebben extra rechten 
          om het weekend te beheren. Deze rol wordt per weekend toegewezen.
        </Text>
        <Text className="text-xs font-semibold text-amber-700 uppercase mb-3">
          Alles van Familielid, plus:
        </Text>
        <Capability text="Teams samenstellen en beheren (leden toewijzen, captains instellen)" />
        <Capability text="Spellen configureren (beschikbaarheid, puntenwaarde, tijdslimieten)" />
        <Capability text="De agenda beheren (evenementen aanmaken, wijzigen en verwijderen)" />
        <Capability text="Onthullingen instellen (locatie, details, countdown)" />
        <Capability text="Uitdagingen en foto-opdrachten aanmaken en beoordelen" />
        <Capability text="Punten handmatig toekennen of aanpassen" />
        <Capability text="Meldingen versturen naar alle familieleden" />
        <Capability text="Live quizzes hosten als quizmaster" />
        <Capability text="Speurtochten opzetten met stappen en QR-codes" />
        <Capability text="Opdrachten met meerdere secties aanmaken" />
      </RoleSection>

      {/* Super Admin */}
      <RoleSection icon="star" title="Super Admin" color="#7C3AED" borderColor="#C4B5FD">
        <Text className="text-sm text-gray-600 leading-5 mb-4">
          Super admins hebben volledige controle over de gehele app. Deze rol is bedoeld voor de 
          technische beheerders van de app.
        </Text>
        <Text className="text-xs font-semibold text-violet-700 uppercase mb-3">
          Alles van Familie Admin, plus:
        </Text>
        <Capability text="Weekenden aanmaken en beheren (naam, datum, locatie, thema)" />
        <Capability text="Gebruikersaccounts beheren (rollen toewijzen, accounts samenvoegen)" />
        <Capability text="Afhankelijke profielen aanmaken voor kinderen" />
        <Capability text="Pre-registraties beheren (familieleden uitnodigen vóór aanmelding)" />
        <Capability text="Activiteitenlogboek bekijken (alle acties in de app)" />
        <Capability text="Systeem-brede instellingen aanpassen" />
        <Capability text="Meldingstemplates en geplande meldingen beheren" />
      </RoleSection>

      {/* Voogden */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Voogden & Afhankelijke Profielen</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Voor jonge kinderen die nog geen eigen account kunnen beheren, is er het systeem van 
        "afhankelijke profielen". Een super admin maakt een profiel aan voor het kind, en een 
        ouder of voogd wordt gekoppeld als beheerder.
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="people"
          title="Voogd (Guardian)"
          description="Een ouder of volwassen familielid dat kan wisselen naar het profiel van hun kind. Ze kunnen namens het kind chatten, spellen spelen en punten verdienen."
          color="#3B82F6"
        />
        <GuideCard
          icon="happy"
          title="Afhankelijk profiel"
          description="Een profiel voor een jong kind zonder eigen e-mailadres. Het kind kan via de voogd deelnemen aan alle activiteiten."
          color="#EC4899"
        />
      </View>

      <GuideTip icon="swap-horizontal" title="Hoe wissel je?">
        Als voogd ga je naar je profiel en tik je op "Wissel naar [naam kind]". Er verschijnt een 
        gele banner bovenaan het scherm die aangeeft dat je als een ander profiel speelt. Tik 
        opnieuw om terug te wisselen naar je eigen profiel.
      </GuideTip>

      <GuideNav {...nav} />
    </View>
  );
}
